import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

import { LOGO_URL } from "@/data/brand";
import { WhatsAppIcon, whatsappUrl, formatWhatsAppNumber } from "@/lib/whatsapp";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMAIL = "kay@kayswastemanagement.co.uk";

const INPUT_CLS =
  "h-11 w-full rounded-none border-2 border-black bg-white px-3 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--kay-pink)]";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email.");
    if (form.message.trim().length < 4) return toast.error("Please add a short message.");

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSent(data);
      toast.success("Message sent!", { description: "We'll be in touch soon." });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[var(--kay-bg)] text-black">
      <header className="border-b-2 border-black bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Kay's Waste Management"
              className="h-11 w-11 rounded-full border-2 border-black bg-black object-cover"
            />
            <div className="leading-none">
              <div className="font-script text-xl text-[var(--kay-pink)]">Kay's</div>
              <div className="font-display text-[13px] uppercase tracking-widest text-[var(--kay-pink)]">
                Waste Management
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 text-xs font-semibold uppercase tracking-widest hard-shadow-sm hover-lift"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>
      <div className="hi-vis-stripe h-3 w-full" />

      <main className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-[var(--kay-pink)]">
          Contact
        </div>
        <h1 className="max-w-3xl font-display text-4xl uppercase leading-none sm:text-5xl md:text-6xl">
          Say hello,
          <br />
          we'd{" "}
          <span className="relative inline-block">
            <span className="relative z-10">love to help.</span>
            <span className="absolute inset-x-[-6px] bottom-1 -z-0 h-3 bg-[var(--kay-pink)]" />
          </span>
        </h1>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Form or success */}
          <div className="lg:col-span-7">
            {sent ? (
              <div
                data-testid="contact-success"
                className="border-2 border-black bg-white p-8 hard-shadow md:p-10"
              >
                <div className="inline-flex h-14 w-14 items-center justify-center border-2 border-black bg-[var(--kay-pink)] text-black">
                  <CheckCircle2 size={26} />
                </div>
                <h2 className="mt-6 font-display text-3xl uppercase leading-tight sm:text-4xl">
                  Message received!
                </h2>
                <p className="mt-3 max-w-xl text-neutral-700">{sent.message}</p>
                <div className="mt-8">
                  <button
                    onClick={() => setSent(null)}
                    className="border-2 border-black bg-white px-5 py-3 font-display text-xs uppercase tracking-widest hard-shadow-sm hover-lift"
                  >
                    Send another
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="border-2 border-black bg-white p-6 hard-shadow md:p-8"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-widest">Your name</Label>
                    <Input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Smith"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest">Email</Label>
                    <Input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="jane@example.co.uk"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest">Phone (optional)</Label>
                    <Input
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="07700 900000"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-widest">Your message</Label>
                    <Textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="How can we help? E.g. old sofa in Weston, need collection this week…"
                      className="min-h-[160px] w-full rounded-none border-2 border-black bg-white px-3 py-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--kay-pink)]"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 border-2 border-black bg-black px-6 py-4 font-display text-sm uppercase tracking-widest text-white hard-shadow-sm hover-lift disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>

          {/* Contact details */}
          <aside className="lg:col-span-5">
            <div className="border-2 border-black bg-white p-6 hard-shadow md:p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
                Chat on WhatsApp
              </div>
              <p className="mt-2 text-sm text-neutral-700">
                Quickest way to reach us — send a photo of your waste and we'll
                give you an estimate.
              </p>
              <a
                href={whatsappUrl("Hi Kay! I'd like to get in touch about a waste collection.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="mt-4 inline-flex items-center gap-3 border-2 border-black bg-[#25D366] px-4 py-3 font-display text-sm uppercase tracking-widest text-black hard-shadow-sm hover-lift"
              >
                <WhatsAppIcon size={18} />
                {formatWhatsAppNumber()}
              </a>

              <div className="mt-8 border-t-2 border-black pt-6">
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
                  Email us
                </div>
                <p className="mt-2 text-sm text-neutral-700">
                  Drop us a line and we'll come back to you as soon as we can.
                </p>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-btn"
                  className="mt-4 inline-flex items-center gap-3 border-2 border-black bg-[var(--kay-pink)] px-4 py-3 font-display text-sm uppercase tracking-widest text-black hard-shadow-sm hover-lift"
                >
                  <Mail size={18} />
                  {EMAIL}
                </a>
              </div>

              <div className="mt-8 border-t-2 border-black pt-6 text-sm text-neutral-600">
                <div className="font-display text-xs uppercase tracking-widest text-black">
                  Areas we cover
                </div>
                <p className="mt-2">
                  Weston-super-Mare, Bristol, Taunton and the wider Somerset
                  area — generally all BS &amp; TA postcodes.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
